/**
 * O cartão **Pessoas** da ficha da Empresa (FR-26) — quem vocês falam nessa empresa.
 *
 * Desde o realinhamento ao protótipo v2 (28/09/2026) cada Pessoa é **uma linha de quatro campos**
 * lado a lado — Nome, Cargo, Telefone, E-mail —, sempre editável: não há botão "Editar" nem um
 * formulário de cadastro separado. "+ Adicionar pessoa" acrescenta uma linha vazia; o "×" da
 * linha nova a descarta antes de gravar. O protótipo chama o telefone de WhatsApp; o serviço só
 * tem `telefone`, e é ele que a linha mostra.
 *
 * **A gravação é explícita, e não no `blur`.** Uma linha que mudou mostra "Salvar" (e
 * "Desfazer") à direita; a linha nova mostra "Salvar" desde que nasce. Gravar a cada campo que
 * perde o foco mandaria ao serviço a Pessoa pela metade — e uma recusa no meio da digitação
 * seria lida como recusa do que a pessoa ainda estava escrevendo.
 *
 * O cartão mora **dentro** da ficha, e só na tela de edição: cada linha grava numa Empresa que já
 * existe. Na ficha **nova** (o "+ Empresa", 13.2) quem mostra as linhas é `PessoasDaFichaNova.tsx`,
 * com os mesmos `QuatroCampos` e o mesmo `corpoDe` daqui, e as Pessoas saem depois de a Empresa
 * nascer.
 *
 * **Nome e telefone são obrigatórios** (13.17, chamado de 26/10/2026, item D: "o telefone, em
 * teoria, sempre teremos, pois é um sistema com WhatsApp conectado"). A tela confere os dois antes
 * de enviar (`recusaDaPessoa`), e o serviço confere de novo (`telefone_obrigatorio`). Telefone e
 * e-mail não ganham validação de formato aqui nem no serviço: inventar um regex recusaria o contato
 * estrangeiro e o ramal que a operação usa. **Desde a 13.2 o telefone tem máscara** (`@/telefone/
 * mascara-do-telefone`): ele se forma enquanto se digita, e o que começa com `+` ou `0`, ou tem
 * letra ou símbolo, fica livre — nunca recusa. O campo mostra o telefone **como gravado**; o que
 * a pessoa não tocou volta igual no `PUT`, e só o que mudou passa por `telefoneParaGravar`.
 * Telefone e e-mail são `type="tel"` e `type="email"` com `autoComplete` por seção (DW-92), e os
 * formulários continuam `noValidate`.
 *
 * **Tudo o que o servidor recusar aparece verbatim.** A recusa dele é a que explica o caso —
 * inclusive o `404` da Empresa que quem lê não vê (FR-30) — e reescrevê-la aqui seria
 * uma segunda fonte de verdade que envelhece calada.
 *
 * **Marcar o principal é a única forma de desmarcar alguém.** Não há botão de "desmarcar": o
 * serviço não tem esse caminho, e uma Empresa com contatos mas sem principal seria um estado
 * que ninguém pediu. Quem já é principal não mostra o botão — mostra a marca. **Não há "×" na
 * Pessoa gravada**: o serviço não tem rota de exclusão de Pessoa.
 *
 * **Depois de cada mutação a lista é relida do serviço**, e não remendada com a resposta:
 * marcar um principal muda **outra** linha além da devolvida, e um remendo deixaria duas
 * marcadas na tela até a próxima navegação.
 *
 * **Quando a lista não pode ser lida, não há o que cadastrar.** O `404` da Empresa (FR-30) é o
 * mesmo que o formulário da ficha já mostrou logo acima; repetir a mensagem e ainda deixar uma
 * linha habilitada cujo envio está garantido a falhar seria convidar a pessoa a digitar quatro
 * campos para receber a mesma recusa.
 *
 * **Paridade de celular (NFR-1).** Não há função exclusiva do desktop e não há árvore
 * alternativa: a linha de quatro colunas vira uma coluna no celular por `useEhCelular`, e as
 * mesmas ações existem nas duas larguras. Sem `@media`.
 */

import { type FormEvent, type ReactNode, useRef, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useEhCelular } from '@/shell/Shell'
import { LinhaDoRotulo } from '@/shell/MarcaDeObrigatorio'
import { aoDigitarTelefone, telefoneParaGravar } from '@/telefone/mascara-do-telefone'
import {
  type ContatoPerguntado,
  type EdicaoDePessoa,
  type Pessoa,
  CONTATO_DO_WHATSAPP_VINCULADO,
  contatoDaPergunta,
  foiAdotada,
  useCadastrarPessoa,
  useEditarPessoa,
  useMarcarPrincipal,
  usePessoas,
} from './api'
import { PerguntaDaAdocao, type RespostaDaAdocao, corpoDaResposta } from './PerguntaDaAdocao'
import './responsaveis-da-empresa.css'

/**
 * Os tetos de coluna do serviço (`servicos/cadastro/dominio/pessoa.py`), pela mesma regra do
 * formulário da ficha: o servidor continua sendo quem decide, e o `maxLength` só evita gastar
 * uma ida à rede com o que o cliente já sabe. Sem eles, colar um nome de trezentos caracteres
 * volta como o `422` genérico do Pydantic, que não nomeia o campo nem o teto.
 */
export const TAMANHO_DO_NOME = 200
const TAMANHO_DO_TELEFONE = 32
const TAMANHO_DO_EMAIL = 320
const TAMANHO_DO_CARGO = 120

export const FALTA_NOME = 'Informe o nome da pessoa.'
/** O telefone vazio (13.17): na linha da ficha nova, no "+ Pessoa" e no formulário de uma Pessoa. */
export const FALTA_TELEFONE = 'Informe o telefone da pessoa.'

/** O campo de que uma recusa fala — o que ganha `aria-invalid` e `aria-describedby`. */
export type CampoDaRecusa = 'nome' | 'telefone' | null

/**
 * Os códigos do serviço que falam do **Telefone** (13.17): o vazio, o repetido na Empresa, o de
 * outra Pessoa, o repetido entre as linhas da ficha nova e o da conversa (o painel).
 */
const CODIGOS_DO_TELEFONE: ReadonlySet<string> = new Set([
  'telefone_obrigatorio',
  'pessoa_ja_cadastrada',
  'telefone_de_outra_pessoa',
  'telefone_repetido_na_ficha',
  'telefone_da_conversa',
])

/**
 * O campo de uma recusa do serviço, **pelo código** — e nunca pela mensagem, que é verbatim. O
 * código que não nomeia campo nenhum não liga campo nenhum: o Nome não é o padrão.
 */
export function campoDoCodigo(codigo: string | null | undefined): CampoDaRecusa {
  return codigo !== null && codigo !== undefined && CODIGOS_DO_TELEFONE.has(codigo)
    ? 'telefone'
    : null
}

/** O campo da falha de uma chamada: o do código do envelope, ou nenhum (rede, `500`). */
export function campoDaFalha(falha: unknown): CampoDaRecusa {
  return falha instanceof ErroDaApi ? campoDoCodigo(falha.codigo) : null
}

/** O campo das recusas locais: `FALTA_NOME` liga o Nome, `FALTA_TELEFONE` o Telefone. */
export function campoDaRecusaLocal(recusa: string | null): CampoDaRecusa {
  if (recusa === FALTA_NOME) {
    return 'nome'
  }
  return recusa === FALTA_TELEFONE ? 'telefone' : null
}

/** Confirmações. Sem elas a pessoa reenvia por não saber se gravou. */
export const CADASTRADA = 'Pessoa cadastrada.'
const SALVA = 'Pessoa salva.'
const MARCADO = 'Principal atualizado.'

const CARREGANDO = 'Carregando as pessoas…'
export const SEM_PESSOAS = 'Esta empresa ainda não tem pessoas cadastradas.'

/** O cartão, com as palavras do protótipo v2. */
export const TITULO = 'Pessoas'
export const NOTA = 'Quem vocês falam nessa empresa. Cadastre aqui mesmo.'
export const ADICIONAR_PESSOA = '+ Adicionar pessoa'

/** `id` do título — é ele que nomeia a `<section>` para o leitor de tela. */
const ID_DO_TITULO = 'responsaveis-titulo'

/** O sufixo dos `id` da linha nova — só ela existe sem `id` de Pessoa. */
const SUFIXO_DA_NOVA = 'nova'

/** O rascunho dos quatro campos — todo campo é texto, porque todo campo é um `input`. */
export interface Rascunho {
  nome: string
  telefone: string
  email: string
  cargo: string
}

export const RASCUNHO_VAZIO: Rascunho = { nome: '', telefone: '', email: '', cargo: '' }

function rascunhoDe(pessoa: Pessoa): Rascunho {
  return {
    nome: pessoa.nome,
    telefone: pessoa.telefone ?? '',
    email: pessoa.email ?? '',
    cargo: pessoa.cargo ?? '',
  }
}

/**
 * O texto sem as marcas invisíveis (`Cf`, o U+200B e o hífen suave do texto colado) e sem os espaços
 * das pontas — a régua única de "vazio" das Pessoas (D-6 da 13.2; o mesmo critério do serviço).
 */
export function semNada(texto: string): string {
  return texto.replace(/\p{Cf}/gu, '').trim()
}

/**
 * Por que este rascunho não sai (13.17): sem nome, `FALTA_NOME`; com nome e sem telefone,
 * `FALTA_TELEFONE`; senão `null`. O nome vem primeiro porque é o primeiro campo da linha.
 */
export function recusaDaPessoa(rascunho: Rascunho): string | null {
  if (semNada(rascunho.nome) === '') {
    return FALTA_NOME
  }
  if (semNada(rascunho.telefone) === '') {
    return FALTA_TELEFONE
  }
  return null
}

/**
 * Campo opcional vazio vai como `null`, e não como `""`: "apagado" é um estado só. Vazio é também
 * o que só tem marcas invisíveis (`Cf`, o U+200B e o hífen suave do texto colado) — a mesma
 * régua da linha em branco da ficha nova (`PessoasDaFichaNova.tsx`, D-6).
 */
function ouNulo(valor: string): string | null {
  const aparado = semNada(valor)
  return aparado === '' ? null : aparado
}

/**
 * O corpo dos quatro campos. O telefone que **difere do gravado** passa por `telefoneParaGravar`
 * (13.2): o formatável sai formatado, a máscara parcial sai só com os dígitos. O que é igual ao
 * gravado volta como está — a Pessoa que ninguém tocou não muda de grafia por um `PUT` de outro
 * campo. Na linha nova não há gravado, e todo telefone passa pela regra.
 */
export function corpoDe(rascunho: Rascunho, telefoneGravado: string | null = null): EdicaoDePessoa {
  const telefone =
    rascunho.telefone === (telefoneGravado ?? '')
      ? rascunho.telefone
      : telefoneParaGravar(rascunho.telefone)
  return {
    nome: rascunho.nome.trim(),
    telefone: ouNulo(telefone),
    email: ouNulo(rascunho.email),
    cargo: ouNulo(rascunho.cargo),
  }
}

/** Se dois rascunhos gravariam a mesma Pessoa — é o que decide se a linha "mudou". */
function iguais(um: Rascunho, outro: Rascunho, telefoneGravado: string | null): boolean {
  const a = corpoDe(um, telefoneGravado)
  const b = corpoDe(outro, telefoneGravado)
  return a.nome === b.nome && a.telefone === b.telefone && a.email === b.email && a.cargo === b.cargo
}

/** O `id` da recusa do cartão (a do serviço), que o campo de que ela fala aponta. */
const ID_DO_ERRO_DO_CARTAO = 'responsaveis-erro'

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

function Campo({
  id,
  rotulo,
  valor,
  aoTrocar,
  maxLength,
  tipo = 'text',
  autoComplete,
  formulario,
  recusadoPor,
  desabilitado,
  obrigatorio = false,
}: {
  id: string
  rotulo: string
  valor: string
  aoTrocar: (valor: string) => void
  maxLength: number
  /** `tel` liga a máscara (13.2) e o teclado de telefone; `email`, o de e-mail (DW-92). */
  tipo?: 'text' | 'tel' | 'email'
  /** O `autoComplete` com a seção da linha (`section-pessoa-<sufixo> tel`, D-12). */
  autoComplete?: string
  /** O `id` do `<form>` quando o campo mora fora dele (as linhas da ficha nova). */
  formulario?: string
  /**
   * `id` da mensagem que recusou **este** campo, quando há uma.
   *
   * É o que liga a recusa ao campo que ela nomeia: sem `aria-invalid` e `aria-describedby`,
   * quem usa leitor de tela ouve "Informe o nome da pessoa" solto e precisa adivinhar qual
   * das linhas (e qual dos quatro campos) a mensagem fala.
   */
  recusadoPor?: string | null
  desabilitado: boolean
  /** O `*` vermelho no rótulo e o `aria-required` no campo (13.15): o Nome e, desde a 13.17, o
   *  Telefone. */
  obrigatorio?: boolean
}) {
  /* O valor quando o campo ganhou o foco: o `blur` só normaliza o telefone que mudou (13.2). */
  const valorNoFoco = useRef(valor)
  const ehTelefone = tipo === 'tel'
  return (
    <div className="responsaveis__campo">
      <LinhaDoRotulo obrigatorio={obrigatorio}>
        <label className="responsaveis__rotulo" htmlFor={id}>
          {rotulo}
        </label>
      </LinhaDoRotulo>
      <input
        id={id}
        className="responsaveis__entrada"
        type={tipo}
        value={valor}
        autoComplete={autoComplete}
        form={formulario}
        maxLength={maxLength}
        disabled={desabilitado}
        aria-invalid={recusadoPor ? true : undefined}
        aria-describedby={recusadoPor ?? undefined}
        aria-required={obrigatorio ? true : undefined}
        onFocus={(evento) => {
          valorNoFoco.current = evento.target.value
        }}
        onBlur={(evento) => {
          const atual = evento.target.value
          if (ehTelefone && atual !== valorNoFoco.current) {
            const gravar = telefoneParaGravar(atual)
            if (gravar !== atual) {
              aoTrocar(gravar)
            }
          }
        }}
        onChange={(evento) => {
          aoTrocar(ehTelefone ? aoDigitarTelefone(evento, valor) : evento.target.value)
        }}
      />
    </div>
  )
}

/**
 * Os quatro campos de uma linha, na ordem do protótipo: Nome, Cargo, Telefone, E-mail.
 *
 * Os `id` levam sufixo porque várias linhas estão na tela ao mesmo tempo: `id` repetido faria
 * o `label` de uma linha apontar para o campo de outra, e quem usa leitor de tela (ou clica no
 * rótulo) acabaria digitando na Pessoa errada.
 *
 * **A variante `telefoneSoLeitura`** (13.17, chamado de 26/10, item M) é a do painel Dados do
 * contato: o Telefone vira texto **no mesmo lugar** (entre Cargo e E-mail), sem campo. Os outros três
 * seguem iguais.
 */
export function QuatroCampos({
  sufixo,
  rascunho,
  aoTrocar,
  recusaDoNome,
  recusaDoTelefone,
  desabilitado,
  autoCompleteDaSecao = `section-pessoa-${sufixo}`,
  formulario,
  telefoneSoLeitura,
  campoDoNome,
}: {
  sufixo: string
  rascunho: Rascunho
  aoTrocar: (campo: keyof Rascunho, valor: string) => void
  /** `id` da mensagem que recusou o Nome, quando há uma. */
  recusaDoNome?: string | null
  /** `id` da mensagem que recusou o Telefone, quando há uma (13.17). */
  recusaDoTelefone?: string | null
  desabilitado: boolean
  /**
   * A seção do `autoComplete` da linha (D-12 da 13.2): com ela o navegador não espalha o contato
   * de quem está digitando na Empresa e em todas as linhas de uma vez.
   */
  autoCompleteDaSecao?: string
  /** O `id` do `<form>` a que os campos pertencem quando moram fora dele (a ficha nova). */
  formulario?: string
  /**
   * O Telefone como texto, sem campo — o painel Dados do contato, onde ele é só leitura (13.17).
   * Ausente, o Telefone é o campo de sempre.
   */
  telefoneSoLeitura?: string
  /**
   * O Nome no lugar do de sempre — o da ficha nova, que sugere as Pessoas cadastradas (13.19c1,
   * `CampoDoNomeComSugestao`). Ausente, o Nome é o campo de texto de sempre.
   */
  campoDoNome?: ReactNode
}) {
  return (
    <>
      {campoDoNome ?? (
      <Campo
        id={`pessoa-nome-${sufixo}`}
        rotulo="Nome"
        //: 13.15: o lugar único do `*` do Nome — cobre a ficha nova, a ficha existente (a linha
        //: nova e a edição) e o "+ Pessoa".
        obrigatorio
        valor={rascunho.nome}
        maxLength={TAMANHO_DO_NOME}
        autoComplete={`${autoCompleteDaSecao} name`}
        formulario={formulario}
        recusadoPor={recusaDoNome}
        desabilitado={desabilitado}
        aoTrocar={(valor) => {
          aoTrocar('nome', valor)
        }}
      />
      )}
      <Campo
        id={`pessoa-cargo-${sufixo}`}
        rotulo="Cargo"
        valor={rascunho.cargo}
        maxLength={TAMANHO_DO_CARGO}
        autoComplete={`${autoCompleteDaSecao} organization-title`}
        formulario={formulario}
        desabilitado={desabilitado}
        aoTrocar={(valor) => {
          aoTrocar('cargo', valor)
        }}
      />
      {telefoneSoLeitura !== undefined ? (
        <div className="responsaveis__campo" data-campo="telefone-so-leitura">
          <span className="responsaveis__rotulo" id={`pessoa-telefone-${sufixo}-rotulo`}>
            Telefone
          </span>
          <span
            className="responsaveis__so-leitura"
            id={`pessoa-telefone-${sufixo}`}
            aria-labelledby={`pessoa-telefone-${sufixo}-rotulo pessoa-telefone-${sufixo}`}
            aria-describedby={recusaDoTelefone ?? undefined}
          >
            {telefoneSoLeitura}
          </span>
        </div>
      ) : (
        <Campo
          id={`pessoa-telefone-${sufixo}`}
          rotulo="Telefone"
          //: 13.17: obrigatório em toda rota de usuário que cria ou edita Pessoa.
          obrigatorio
          valor={rascunho.telefone}
          tipo="tel"
          autoComplete={`${autoCompleteDaSecao} tel`}
          formulario={formulario}
          maxLength={TAMANHO_DO_TELEFONE}
          recusadoPor={recusaDoTelefone}
          desabilitado={desabilitado}
          aoTrocar={(valor) => {
            aoTrocar('telefone', valor)
          }}
        />
      )}
      <Campo
        id={`pessoa-email-${sufixo}`}
        rotulo="E-mail"
        valor={rascunho.email}
        tipo="email"
        autoComplete={`${autoCompleteDaSecao} email`}
        formulario={formulario}
        maxLength={TAMANHO_DO_EMAIL}
        desabilitado={desabilitado}
        aoTrocar={(valor) => {
          aoTrocar('email', valor)
        }}
      />
    </>
  )
}

/**
 * Os `recusaDoNome` e `recusaDoTelefone` de uma linha: a recusa local (o `id` dela, na linha) ou a
 * do serviço (o `id` do rodapé do cartão), cada uma no campo de que fala — e nenhuma no Nome por
 * padrão.
 */
function ligacoesDaRecusa(
  campoLocal: CampoDaRecusa,
  idLocal: string,
  campoDoErro: CampoDaRecusa,
): { recusaDoNome: string | null; recusaDoTelefone: string | null } {
  const idDo = (campo: 'nome' | 'telefone'): string | null =>
    campoLocal === campo ? idLocal : campoDoErro === campo ? ID_DO_ERRO_DO_CARTAO : null
  return { recusaDoNome: idDo('nome'), recusaDoTelefone: idDo('telefone') }
}

/**
 * Uma Pessoa gravada: os quatro campos sempre editáveis, a marca de principal e as ações.
 *
 * O rascunho só existe **enquanto a linha mudou**: sem mudança, o que a linha mostra é a
 * Pessoa que o serviço tem — e por isso uma recarga da lista (marcar principal, salvar outra
 * linha) nunca deixa esta linha mostrando um valor velho. A primeira tecla parte do que está
 * gravado agora, e não de uma cópia tirada na montagem.
 */
function LinhaDaPessoa({
  pessoa,
  aoSalvar,
  aoMarcar,
  ocupado,
  salvando,
  campoDoErro = null,
}: {
  pessoa: Pessoa
  aoSalvar: (corpo: EdicaoDePessoa, aoConcluir: () => void) => void
  aoMarcar: () => void
  ocupado: boolean
  /** A **edição desta linha** está em voo — e não qualquer escrita do cartão. */
  salvando: boolean
  /** O campo de que a recusa do serviço (no rodapé do cartão) fala, quando ela é desta linha. */
  campoDoErro?: CampoDaRecusa
}) {
  const [rascunho, setRascunho] = useState<Rascunho | null>(null)
  const [recusa, setRecusa] = useState<string | null>(null)
  const idDaRecusa = `responsaveis-recusa-${pessoa.id}`
  const gravado = rascunhoDe(pessoa)
  const exibido = rascunho ?? gravado
  const mudou = rascunho !== null && !iguais(rascunho, gravado, pessoa.telefone)

  function trocar(campo: keyof Rascunho, valor: string) {
    setRecusa(null)
    setRascunho({ ...exibido, [campo]: valor })
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (!mudou) {
      return
    }
    const local = recusaDaPessoa(exibido)
    if (local !== null) {
      setRecusa(local)
      return
    }
    aoSalvar(corpoDe(exibido, pessoa.telefone), () => {
      setRascunho(null)
    })
  }

  return (
    <li className="responsaveis__item">
      <form
        className="responsaveis__linha"
        aria-label={`Pessoa ${pessoa.nome}`}
        onSubmit={enviar}
        noValidate
      >
        <QuatroCampos
          sufixo={pessoa.id}
          rascunho={exibido}
          aoTrocar={trocar}
          {...ligacoesDaRecusa(campoDaRecusaLocal(recusa), idDaRecusa, campoDoErro)}
          desabilitado={ocupado}
        />
        <div className="responsaveis__acoes">
          {mudou ? (
            <>
              {/* O rótulo segue a **edição desta linha**, e o `disabled` segue o cartão inteiro:
                  gravar outra linha ocupa esta, mas não é ela que está gravando — e um botão
                  que diz "Salvando…" sem estar salvando mente para o leitor de tela. O nome vai
                  no `aria-label` porque há um "Salvar" por linha. */}
              <button
                className="responsaveis__botao"
                type="submit"
                disabled={ocupado}
                aria-label={`Salvar ${pessoa.nome}`}
              >
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
              <button
                className="responsaveis__acao"
                type="button"
                disabled={ocupado}
                aria-label={`Desfazer a edição de ${pessoa.nome}`}
                onClick={() => {
                  // De volta ao que está gravado: sem isto, a linha continuaria mostrando o que
                  // a pessoa acabou de decidir descartar.
                  setRascunho(null)
                  setRecusa(null)
                }}
              >
                Desfazer
              </button>
            </>
          ) : pessoa.principal ? (
            /* A marca é texto, e não só cor: quem não distingue as duas cores precisa ler qual
               contato é o principal. */
            <span className="responsaveis__marca">Principal</span>
          ) : (
            /*
             * O nome vai no `aria-label`, e não no rótulo visível: numa lista de contatos há um
             * botão "Tornar principal" por linha, e sem o nome o leitor de tela anunciaria N
             * botões idênticos.
             */
            <button
              className="responsaveis__acao"
              type="button"
              disabled={ocupado}
              aria-label={`Tornar ${pessoa.nome} principal`}
              onClick={aoMarcar}
            >
              Tornar principal
            </button>
          )}
        </div>
        {recusa === null ? null : (
          <p className="responsaveis__erro responsaveis__erro--da-linha" id={idDaRecusa} role="alert">
            {recusa}
          </p>
        )}
      </form>
    </li>
  )
}

/** A linha nova: os quatro campos vazios, "Salvar" e o "×" que a descarta sem gravar. */
function LinhaNova({
  aoSalvar,
  aoDescartar,
  aoEditar,
  ocupado,
  salvando,
  campoDoErro = null,
}: {
  aoSalvar: (corpo: EdicaoDePessoa) => void
  aoDescartar: () => void
  /** A linha mudou: a pergunta da adoção (do corpo anterior) deixa de valer. */
  aoEditar: () => void
  ocupado: boolean
  salvando: boolean
  /** O campo de que a recusa do serviço (no rodapé do cartão) fala. */
  campoDoErro?: CampoDaRecusa
}) {
  const [rascunho, setRascunho] = useState<Rascunho>(RASCUNHO_VAZIO)
  const [recusa, setRecusa] = useState<string | null>(null)
  const idDaRecusa = `responsaveis-recusa-${SUFIXO_DA_NOVA}`

  function trocar(campo: keyof Rascunho, valor: string) {
    setRecusa(null)
    aoEditar()
    setRascunho((atual) => ({ ...atual, [campo]: valor }))
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const local = recusaDaPessoa(rascunho)
    if (local !== null) {
      setRecusa(local)
      return
    }
    aoSalvar(corpoDe(rascunho))
  }

  return (
    <li className="responsaveis__item">
      <form className="responsaveis__linha" aria-label="Nova pessoa" onSubmit={enviar} noValidate>
        <QuatroCampos
          sufixo={SUFIXO_DA_NOVA}
          rascunho={rascunho}
          aoTrocar={trocar}
          {...ligacoesDaRecusa(campoDaRecusaLocal(recusa), idDaRecusa, campoDoErro)}
          desabilitado={ocupado}
        />
        <div className="responsaveis__acoes">
          <button
            className="responsaveis__botao"
            type="submit"
            disabled={ocupado}
            aria-label="Salvar a nova pessoa"
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
          <button
            className="responsaveis__remover"
            type="button"
            disabled={ocupado}
            aria-label="Descartar a nova pessoa"
            title="Descartar"
            onClick={aoDescartar}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        {recusa === null ? null : (
          <p className="responsaveis__erro responsaveis__erro--da-linha" id={idDaRecusa} role="alert">
            {recusa}
          </p>
        )}
      </form>
    </li>
  )
}

export interface PropriedadesDosResponsaveis {
  /** A Empresa dona dos contatos. A seção só existe na tela de edição, então ela sempre existe. */
  empresaId: string
  /**
   * A confirmação com que a seção abre — o aviso da adoção que o "+ Empresa" trouxe até a ficha
   * (13.4): a linha de Pessoa da ficha nova completou a Pessoa solta do WhatsApp.
   */
  avisoInicial?: string
}

export function ResponsaveisDaEmpresa({ empresaId, avisoInicial }: PropriedadesDosResponsaveis) {
  const ehCelular = useEhCelular()
  const consulta = usePessoas(empresaId)
  const cadastro = useCadastrarPessoa(empresaId)
  const edicao = useEditarPessoa(empresaId)
  const marcacao = useMarcarPrincipal(empresaId)

  const [novaAberta, setNovaAberta] = useState(false)
  /** A Pessoa cuja edição está em voo — para o "Salvando…" ficar só na linha dela. */
  const [salvandoA, setSalvandoA] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  /** De que linha (o `id` da Pessoa, ou a nova) e de que campo a recusa do serviço fala (13.17). */
  const [alvoDoErro, setAlvoDoErro] = useState<{ linha: string; campo: CampoDaRecusa } | null>(
    null,
  )
  const [confirmacao, setConfirmacao] = useState<string | null>(avisoInicial ?? null)
  /*
   * A pergunta da adoção (13.14a, DW-388): o contato do WhatsApp com outro nome e o corpo que a
   * provocou — a resposta reenvia o mesmo corpo com `adocao`.
   */
  const [pergunta, setPergunta] = useState<{
    contato: ContatoPerguntado
    corpo: EdicaoDePessoa
  } | null>(null)

  const ocupado = cadastro.isPending || edicao.isPending || marcacao.isPending
  const pessoas = consulta.data ?? []

  /**
   * Os avisos falam do envio que está em voo — nunca do anterior.
   *
   * Sem isto, a recusa de uma gravação continua na tela enquanto a próxima corre, e como o
   * `role="status"` da confirmação só aparece com `erro === null`, o sucesso seguinte ficaria
   * mudo até que a mudança de estado o limpasse.
   */
  function limparOsAvisos() {
    setErro(null)
    setAlvoDoErro(null)
    setConfirmacao(null)
    setPergunta(null)
  }

  /** `linha`: de quem a recusa fala — o `id` da Pessoa, `SUFIXO_DA_NOVA`, ou nenhuma (marcar). */
  function aoFalhar(falha: unknown, linha: string | null = null) {
    setConfirmacao(null)
    setErro(mensagemDe(falha))
    setAlvoDoErro(linha === null ? null : { linha, campo: campoDaFalha(falha) })
  }

  function campoDoErroDe(linha: string): CampoDaRecusa {
    return erro !== null && alvoDoErro?.linha === linha ? alvoDoErro.campo : null
  }

  function cadastrar(corpo: EdicaoDePessoa, resposta: RespostaDaAdocao | null = null) {
    limparOsAvisos()
    cadastro.mutate(
      // `principal: false` explícito: quem cadastra não rouba a marca de quem já a tem, e
      // marcar é um ato à parte — com botão próprio e registro próprio na trilha.
      { ...corpo, principal: false, ...corpoDaResposta(resposta) },
      {
        onSuccess: (cadastrada) => {
          setErro(null)
          // A adoção da Pessoa solta (13.4): o telefone era o de um contato do WhatsApp que quem
          // cadastra recebeu, e **ele** entrou na ficha — a tela diz isso, e não "cadastrada".
          setConfirmacao(foiAdotada(cadastrada) ? CONTATO_DO_WHATSAPP_VINCULADO : CADASTRADA)
          setNovaAberta(false)
        },
        onError: (falha) => {
          aoFalhar(falha, SUFIXO_DA_NOVA)
          // O contato com outro nome (13.14a): a mensagem verbatim, e a pergunta embaixo dela.
          const contato = contatoDaPergunta(falha)
          if (contato !== undefined) {
            setPergunta({ contato, corpo })
          }
        },
      },
    )
  }

  function salvar(pessoaId: string, corpo: EdicaoDePessoa, aoConcluir: () => void) {
    limparOsAvisos()
    setSalvandoA(pessoaId)
    edicao.mutate(
      { pessoaId, pessoa: corpo },
      {
        onSuccess: () => {
          setErro(null)
          setConfirmacao(SALVA)
          aoConcluir()
        },
        onError: (falha) => {
          aoFalhar(falha, pessoaId)
        },
        onSettled: () => {
          setSalvandoA(null)
        },
      },
    )
  }

  function marcar(pessoaId: string) {
    limparOsAvisos()
    marcacao.mutate(pessoaId, {
      onSuccess: () => {
        setErro(null)
        setConfirmacao(MARCADO)
      },
      onError: (falha) => {
        aoFalhar(falha)
      },
    })
  }

  return (
    <section
      className="responsaveis"
      aria-labelledby={ID_DO_TITULO}
      data-largura={ehCelular ? 'celular' : 'computador'}
    >
      <div className="responsaveis__cartao">
        <div className="responsaveis__cabecalho">
          <h2 className="responsaveis__titulo" id={ID_DO_TITULO}>
            {TITULO}
          </h2>
          <span className="responsaveis__ajuda">{NOTA}</span>
        </div>

        <div className="responsaveis__corpo">
          {consulta.isError ? (
            /* Verbatim: o `404` do serviço é o mesmo para Empresa inexistente e para Empresa
               que a RLS esconde (FR-30), e é ele que sabe explicar o caso. */
            <p className="responsaveis__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : /* `isLoading`, e não `isPending`: uma consulta desabilitada (`empresaId` vazio)
                 fica `pending` para sempre no React Query v5, e a seção travaria em
                 "Carregando…" sem nunca ter saído nenhuma requisição. */
          consulta.isLoading ? (
            <p className="responsaveis__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : (
            <>
              {pessoas.length === 0 && !novaAberta ? (
                <p className="responsaveis__ajuda">{SEM_PESSOAS}</p>
              ) : null}
              <ul className="responsaveis__lista" aria-label={TITULO}>
                {pessoas.map((pessoa) => (
                  <LinhaDaPessoa
                    key={pessoa.id}
                    pessoa={pessoa}
                    ocupado={ocupado}
                    salvando={edicao.isPending && salvandoA === pessoa.id}
                    campoDoErro={campoDoErroDe(pessoa.id)}
                    aoSalvar={(corpo, aoConcluir) => {
                      salvar(pessoa.id, corpo, aoConcluir)
                    }}
                    aoMarcar={() => {
                      marcar(pessoa.id)
                    }}
                  />
                ))}
                {novaAberta ? (
                  <LinhaNova
                    ocupado={ocupado}
                    salvando={cadastro.isPending}
                    campoDoErro={campoDoErroDe(SUFIXO_DA_NOVA)}
                    aoSalvar={cadastrar}
                    aoDescartar={() => {
                      setNovaAberta(false)
                      setErro(null)
                      setPergunta(null)
                    }}
                    aoEditar={() => {
                      // 13.14a: a pergunta respondia ao corpo enviado; editado, ele não vale mais.
                      if (pergunta !== null) {
                        setPergunta(null)
                        setErro(null)
                      }
                    }}
                  />
                ) : null}
              </ul>
              {/* Uma linha nova por vez: com ela aberta, o link some até ela ser gravada ou
                  descartada. */}
              {novaAberta ? null : (
                <div>
                  <button
                    className="responsaveis__acao"
                    type="button"
                    disabled={ocupado}
                    onClick={() => {
                      limparOsAvisos()
                      setNovaAberta(true)
                    }}
                  >
                    {ADICIONAR_PESSOA}
                  </button>
                </div>
              )}
            </>
          )}

          {erro === null ? null : (
            <p className="responsaveis__erro" role="alert" id={ID_DO_ERRO_DO_CARTAO}>
              {erro}
            </p>
          )}
          {pergunta === null ? null : (
            <PerguntaDaAdocao
              contato={pergunta.contato}
              desabilitada={ocupado}
              aoResponder={(resposta) => {
                cadastrar(pergunta.corpo, resposta)
              }}
            />
          )}
          {/* `role="status"` e não `role="alert"`: deu certo, e o leitor de tela não precisa
              interromper o que a pessoa estiver fazendo para dizer isso. */}
          {confirmacao === null || erro !== null ? null : (
            <p className="responsaveis__sucesso" role="status">
              {confirmacao}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
